import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { newAttemptNonce } from '../../lib/attempt'
import { seatOverride } from '../../lib/seat'
import { useSessionFeed } from '../../lib/useSessionFeed'
import type { SessionState } from '../../lib/contract'
import { findScenarioByPack, type Scenario } from '../../content/scenarios'
import type { FeedLine } from './parts/Feed'
import type { CurtainCard } from './parts/Curtain'

/**
 * Everything the Inhabit surface KNOWS. Nothing about what it shows.
 *
 * The engine runs the perception-first strategy here, so the character's feed
 * arrives as `message` events rather than narrator prose blobs. The client is
 * dumb: it renders what arrives and decides nothing (inhabit-format §2.3).
 *
 * Device-blind by design — see surfaces/desk/useDeskSession.ts for the same
 * reasoning. The will line is where a phone surface will most want to differ,
 * because composing prose with a thumb is the expensive act.
 */

/** The engine's default player-character when the pack does not name one. */
const DEFAULT_PC = 'a person, here as themselves'

export interface InhabitSession {
  packId: string
  scenario: Scenario | undefined

  sessionId: string | null
  starting: boolean
  error: string | null
  begin: () => void

  lines: FeedLine[]
  thinking: boolean
  card: CurtainCard | null
  dismissCard: () => void

  standing: SessionState['standingState'] | null
  doing: string | null | undefined
  affordances: string[]
  terminated: boolean
  terminationReason: string | null

  submit: (text: string) => void
}

export function useInhabitSession(
  packIdOverride?: string,
  /** ⚠ The price the player agreed to at the threshold, in micros. See `Threshold.tsx`. */
  agreedPriceMicros?: number,
): InhabitSession {
  const routeParams = useParams()
  const packId = packIdOverride ?? routeParams.packId ?? ''
  const [params, setParams] = useSearchParams()
  const scenario = findScenarioByPack(packId)

  const [sessionId, setSessionId] = useState<string | null>(params.get('session'))
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [lines, setLines] = useState<FeedLine[]>([])
  const [thinking, setThinking] = useState(false)
  const [card, setCard] = useState<CurtainCard | null>(null)
  const [state, setState] = useState<SessionState | null>(null)
  const [affordances, setAffordances] = useState<string[]>([])

  // StrictMode mounts effects twice in development; without this guard that
  // would start two sessions and bill two openings.
  const startGuard = useRef(false)
  const cardSeq = useRef(0)

  /*
   * ⚠ ONE OPEN AT A TIME — a ref, not the `starting` state (bug, 2026-08-27).
   *
   * `requestId` used to be the constant `inhabit:${packId}`, which meant every visit after the
   * first silently reused the first visit's entitlement (`lib/attempt.ts`). Fixing it to a
   * per-attempt nonce removes the accidental double-tap protection that constant was providing, so
   * the protection has to become deliberate — otherwise the fix trades never-charging for charging
   * twice, which is the worse of the two.
   *
   * `starting` cannot do this job: a state update is not visible to a second click in the same
   * tick, so two rapid taps both read `starting === false` and both open. A ref is written
   * synchronously and is.
   */
  const opening = useRef(false)
  /** The purchase key for the sitting being opened. Spent by a success, kept across failures. */
  const attemptKey = useRef<string>('')

  // ---------------------------------------------------------------- start

  const begin = useCallback(async () => {
    if (opening.current) return
    opening.current = true
    setStarting(true)
    setError(null)
    /*
     * ⚠ THE NONCE SURVIVES A FAILURE AND IS SPENT ONLY BY A SUCCESS. A fresh one per button press
     * would be wrong in the one place it matters: if an open failed *after* the hold was taken, the
     * retry would take a SECOND hold, and the sweeper settles an orphaned one at `Settle(0)` — the
     * pack-start. Two presses, two pack-starts, for one sitting the guest never got.
     *
     * Holding the key across failures makes the retry idempotent against the orphan instead: the
     * wallet recognises it and hands back the entitlement already paid for. Clearing it on success
     * is what keeps the NEXT sitting a new one, which is the whole point of the fix.
     */
    if (!attemptKey.current) attemptKey.current = newAttemptNonce()
    try {
      const res = await api.startSession({
        packId,
        playerCharacterContent: params.get('pc') ?? DEFAULT_PC,
        strategy: 'PerceptionFirst',
        agreedPriceMicros,
        requestId: `inhabit:${packId}:${attemptKey.current}`,
        // The player's clock, for a pack that runs on it (2026-09-11).
        utcOffsetMinutes: -new Date().getTimezoneOffset(),
        ...seatOverride(),
      })
      attemptKey.current = ''
      setSessionId(res.sessionId)
      // Keep the session in the URL so a refresh resumes rather than restarts.
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('session', res.sessionId)
          return next
        },
        { replace: true },
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the session.')
    } finally {
      setStarting(false)
      // Released on both paths. The guard's job is the in-flight window — the burst of taps while
      // the request is open; by the time it resolves, a success has replaced the threshold and a
      // failure is showing an error the guest has to act on. Latching it shut after success would
      // instead deadlock the one real recovery path: `getState` failing puts the surface back at
      // the threshold (see the effect below), and `begin` has to work there.
      opening.current = false
    }
  }, [packId, params, setParams])

  useEffect(() => {
    if (!sessionId || startGuard.current) return
    startGuard.current = true
    void api
      .getState(sessionId)
      .then(setState)
      .catch(() => {
        // Sessions live in memory; a restart drops them from the registry. Fall
        // back to the threshold rather than showing a dead surface.
        setSessionId(null)
        startGuard.current = false
      })
  }, [sessionId])

  // ---------------------------------------------------------------- feed

  const pushLine = useCallback((line: FeedLine) => {
    // The column is trimmed: the arriving edge is what matters, and an
    // unbounded list would degrade the reveal timing over a long visit.
    setLines((prev) => [...prev, line].slice(-120))
  }, [])

  useSessionFeed(sessionId, {
    message: (m) => {
      if (!m.content) return
      setThinking(false)
      pushLine({
        id: m.messageId,
        text: m.content,
        channel: m.channelId ?? 'narration',
        speaker: m.senderKind === 'Npc' ? m.senderLabel : undefined,
        fromPlayer: m.senderKind === 'Player',
        revealMs: m.revealDurationMs ?? 0,
        at: m.fictionalTimestamp,
      })
    },
    'chunk-gen-started': () => setThinking(true),
    'scene-card': (c) => {
      if (!c.text) return
      cardSeq.current += 1
      setCard({ key: cardSeq.current, text: c.text, holdMs: c.holdMs })
    },
    error: (e) => setError(e.message),
  })

  // ---------------------------------------------------------------- polls

  // The margin and the affordance field are pull-side: they are state, not
  // events, and the engine has no push channel for them yet.
  useEffect(() => {
    if (!sessionId) return
    let alive = true

    const tick = async () => {
      try {
        const [s, a] = await Promise.all([
          api.getState(sessionId),
          api.getAffordances(sessionId).catch(() => null),
        ])
        if (!alive) return
        setState(s)
        if (a) setAffordances(a.affordances ?? [])
      } catch {
        /* transient — the feed is the live channel, this is only the bank */
      }
    }

    void tick()
    const id = window.setInterval(tick, 6000)
    return () => {
      alive = false
      window.clearInterval(id)
    }
  }, [sessionId])

  // ---------------------------------------------------------------- will

  const submit = useCallback(
    async (text: string) => {
      if (!sessionId) return
      // Echo the will immediately: it is the player's act, and waiting for the
      // engine to reflect it would make the surface feel unresponsive.
      pushLine({
        id: `will-${Date.now()}`,
        text,
        channel: 'will',
        fromPlayer: true,
        revealMs: 0,
      })
      setThinking(true)
      try {
        await api.sendInput(sessionId, text)
      } catch (e) {
        setThinking(false)
        setError(e instanceof Error ? e.message : 'That did not reach the world.')
      }
    },
    [sessionId, pushLine],
  )

  return {
    packId,
    scenario,

    sessionId,
    starting,
    error,
    begin: () => void begin(),

    lines,
    thinking,
    card,
    dismissCard: () => setCard(null),

    standing: state?.standingState ?? null,
    doing: state?.standingIntent,
    affordances,
    terminated: state?.terminated ?? false,
    terminationReason: state?.terminationReason ?? null,

    submit: (t: string) => void submit(t),
  }
}
